import Role from "@/types/Role";
import Permission from "@/types/Permission";
import { useEffect, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import ColumnHeader from "@/components/atoms/ColumnHeader";
import { Switch } from "@/components/atoms/Switch";
import PageHeader from "@/components/molecules/PageHeader";
import Button from "@/components/atoms/Button";
import { Plus, X } from "lucide-react";
import Container from "@/components/atoms/Container";
import { zodResolver } from "@hookform/resolvers/zod";
import RoleSchema from "@/schemas/role";
import { router } from "@inertiajs/react";
import Input from "@/components/atoms/Input";
import Tooltip from "@/components/atoms/Tooltip";
import clsx from "clsx";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/molecules/Sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import useMessageProps from "@/hooks/useMessageProps";

// Types
interface Props {
  roles: Role[];
  all_permissions: Permission[];
  redirect_props?: {
    success: boolean;
    message?: string;
    errors?: { [key: string]: string };
  };
}

const Index = ({ roles, all_permissions, redirect_props, ...props }: Props) => {
  const [open, setOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);
  const [mode, setMode] = useState<"edit" | "create">("edit");
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(roles[0]);
  const [selected, setSelected] = useState<string[]>(roles[0].permissions.map((permission) => `${permission.id}`) || []);
  const [searchQuery, setSearchQuery] = useState("");

  // Group permissions by their controllers
  const permissionGroups: Record<string, Permission[]> = all_permissions.reduce<Record<string, Permission[]>>((acc, permission) => {
    if (!acc[permission.controller]) {
      acc[permission.controller] = [];
    }
    acc[permission.controller].push(permission);
    return acc;
  }, {});

  const handleRoleSelect = (role: Role) => {
    setSelectedRole(role);
    setSelected(role.permissions.map((permission) => `${permission.id}`));
  };

  const handleCheck = (checked: boolean, id: string) => {
    setSelected(
      checked ? [...selected, id] : selected.filter((select) => select !== id)
    );
  };

  useMessageProps({ redirect_props });

  const handleSave = () => {
    if (selectedRole) {
      router.post(
        `/roles/${selectedRole.id}/set_role_permissions`,
        {
          data: selected,
        },
        {
          onStart: () => setLoading(true),
          onFinish: () => setLoading(false),
        }
      );
    }
  };

  const form = useForm({
    resolver: zodResolver(RoleSchema),
    defaultValues: currentRole || {
      name: "",
      has_all_accounts_access: false,
    },
  });

  const onSubmit = (data: Partial<Role>) => {
    if (mode === "create") {
      router.post("/roles", data as any, {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      });
    } else {
      router.put(`/roles/${currentRole?.id}`, data as any, {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      });
    }
  };

  useEffect(() => {
    if (open && currentRole) {
      form.reset(currentRole);
    } else if (currentRole) {
      setCurrentRole(null);
      form.reset();
    }
  }, [open, currentRole]);

  

  // Filter Permissions Based on Search Query
  const filteredPermissions = all_permissions.filter((permission) =>
    permission.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Container className="py-4">
        <PageHeader title="Roles & Permissions">
          <div className="space-x-2">
            <Button
              className="inline-flex items-center gap-2 text-black"
              onClick={() => {
                setMode("create");
                setEdit(true);
                setOpen(true);
                form.reset();
              }}
            >
              <Plus size={16} /> <span>Add Role</span>
            </Button>
          </div>
        </PageHeader>
        <div className="border-b border-border" />
        <div className="flex">
          {/* Sidebar for Role Selection */}
          <div className="w-1/4 p-4 border-r border-border">
            <h3 className="mb-2 font-bold">Roles</h3>
            {roles.map((role) => (
              <button
                key={role.id}
                className={clsx(
                  "block w-full text-left p-2 rounded mb-2",
                  selectedRole?.id === role.id ? "bg-primary text-black" : "bg-gray-200 dark:bg-slate-700 hover:ring-2 ring-primary"
                )}
                onClick={() => handleRoleSelect(role)}
              >
                {role.name}
              </button>
            ))}
          </div>

          {/* Main Content */}
          <div className="w-3/4 p-4">
            {/* Search Bar */}
            <Input
              placeholder="Search permissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-4"
            />

            {/* Permissions List Grouped by Category */}
            {Object.entries(permissionGroups).map(([controller, permissions]) => (
              <div key={controller} className="mb-4">
                <h4 className="font-bold mb-2">{controller}</h4>
                {permissions.filter((permission) =>
                  permission.name.toLowerCase().includes(searchQuery.toLowerCase())
                ).map((permission) => (
                  <div key={permission.id} className="flex items-center justify-between mb-2">
                    <Tooltip content={permission.description}>
                      <span>{permission.name}</span>
                    </Tooltip>
                    <Switch
                      checked={selected.includes(`${permission.id}`)}
                      onCheckedChange={(checked) => handleCheck(!!checked, `${permission.id}`)}
                    />
                  </div>
                ))}
              </div>
            ))}

            <Button onClick={handleSave} className="mt-4">
              Save
            </Button>
          </div>
        </div>
      </Container>

      <Sheet
        open={open}
        onOpenChange={(opened) => {
          setOpen(opened);
          if (!opened) {
            form.reset();
            setEdit(false);
          }
        }}
      >
        <SheetContent>
          <SheetHeader>
            <SheetTitle>
              <div className="flex items-center justify-between mt-6">
                <span>{currentRole?.name || "Create"}</span>
                {mode !== "create" && (
                  <Button
                    type="button"
                    onClick={() => {
                      if (edit) form.reset();
                      setEdit(!edit);
                    }}
                  ></Button>
                )}
              </div>
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-8"
              >
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name *</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={!edit} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="has_all_accounts_access"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>All Account Access *</FormLabel>
                      <FormControl>
                        <div>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={!edit}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {(edit || mode === "create") && (
                  <Button type="submit" disabled={loading}>
                    Submit
                  </Button>
                )}
              </form>
            </Form>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Index;
